import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useFavorites } from '@/hooks/useFavorites'

const navItems = [
  { to: '/', label: '首页', icon: 'ri-home-5-line' },
  { to: '/category', label: '影视分类', icon: 'ri-layout-grid-line' },
  { to: '/about', label: '关于我们', icon: 'ri-information-line' },
]

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [keyword, setKeyword] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const { favorites } = useFavorites()

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname, location.search])

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const value = keyword.trim()
    if (!value) return
    navigate(`/search?q=${encodeURIComponent(value)}`)
    setKeyword('')
  }

  return (
    <header className="sticky top-0 z-50">
      <div className="hidden bg-primary-950 text-background-200 md:block">
        <div className="page-shell flex h-8 items-center justify-between text-[11px] tracking-wide">
          <p className="flex items-center gap-2">
            <i className="ri-fire-line text-accent-400" />
            今日更新 3 部新作 · 全站示例内容，仅作演示
          </p>
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5">
              <i className="ri-heart-3-line text-accent-400" />
              已收藏 {favorites.length} 部
            </span>
            <Link to="/about" className="transition-colors hover:text-accent-300">
              版权与免责声明
            </Link>
          </div>
        </div>
      </div>

      <div className="border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="page-shell flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-900 text-background-50">
              <i className="ri-play-fill text-lg text-accent-400" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-[17px] font-bold tracking-tight text-foreground-950">
                麻豆传媒<span className="text-accent-600">在线</span>
              </span>
              <span className="mt-1 text-[10px] tracking-[0.28em] text-foreground-500">
                MADOU ONLINE
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `relative flex h-16 items-center px-4 text-sm transition-colors ${
                    isActive
                      ? 'font-medium text-primary-800 after:absolute after:bottom-0 after:left-4 after:right-4 after:h-0.5 after:rounded-full after:bg-accent-500'
                      : 'text-foreground-600 hover:text-primary-800'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={handleSearch}
              className="hidden items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 lg:flex"
            >
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索作品、导演、演员"
                aria-label="搜索影视作品"
                className="w-40 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              <button
                type="submit"
                className="cursor-pointer rounded-full bg-primary-800 px-3 py-1 text-xs text-background-50 transition-colors hover:bg-primary-700"
              >
                搜索
              </button>
            </form>

            <Link
              to="/category"
              className="hidden whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 sm:block"
            >
              全部作品
            </Link>

            <Link
              to="/search"
              aria-label="搜索影视作品"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
            >
              <i className="ri-search-line text-lg" />
            </Link>

            <button
              type="button"
              aria-label="打开导航菜单"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="border-b border-background-200 bg-background-50 md:hidden">
          <div className="page-shell py-4">
            <form onSubmit={handleSearch} className="mb-4 flex items-center gap-2">
              <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2">
                <i className="ri-search-line text-sm text-foreground-500" />
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索影视作品"
                  aria-label="搜索影视作品"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-accent-500 px-4 py-2 text-sm font-medium text-background-50"
              >
                搜索
              </button>
            </form>
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 border-b border-background-200 py-3 text-sm last:border-0 ${
                      isActive ? 'font-medium text-accent-700' : 'text-foreground-700'
                    }`
                  }
                >
                  <i className={`${item.icon} text-base`} />
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  )
}